import React, { useState, useEffect, useRef } from 'react';
import { createGuide, updateGuide, publishGuide as publishGuideOperation, getDraftGuide, getPublishedGuide, deleteAllDrafts } from '../../utils/guideOperations';
import ReactMarkdown from 'react-markdown';
import { storage } from '../../firebase/config';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

const GuideEditor = ({ itemId, itemName, onClose, onSave, isAdmin = false }) => {
  const [guideData, setGuideData] = useState({
    title: itemName || '',
    content: '',
    images: [],
    newImages: [],
    hazards: [],
    ppe: [],
    quickFacts: {
      powerLimits: '',
      serialNumber: '',
      calibrationDue: '',
      weight: '',
      dimensions: ''
    }
  });
  
  const [imageFiles, setImageFiles] = useState([]);
  const [newImageFiles, setNewImageFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isDraft, setIsDraft] = useState(false);
  const [existingGuideId, setExistingGuideId] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [tempUrls, setTempUrls] = useState([]);
  
  const fileInputRef = useRef(null);
  const newFileInputRef = useRef(null);
  const contentRef = useRef(null);

  // Predefined hazard and PPE options
  const hazardOptions = [
    'Electrical Shock', 'High Voltage', 'Moving Parts', 'Hot Surfaces', 
    'Chemical Exposure', 'Radiation', 'Laser Light', 'Sharp Objects',
    'Heavy Equipment', 'Noise', 'UV Light', 'Compressed Gas'
  ];

  const ppeOptions = [
    'Safety Glasses', 'Lab Coat', 'Gloves', 'Face Shield', 
    'Hearing Protection', 'Respirator', 'Steel Toe Boots', 'Hard Hat'
  ];

  useEffect(() => {
    loadExistingDraft();
  }, [itemId]);

  useEffect(() => {
    return () => {
      tempUrls.forEach(url => URL.revokeObjectURL(url));
    };
  }, [tempUrls]);

  const loadExistingDraft = async () => {
    try {
      const draft = await getDraftGuide(itemId);
      if (draft) {
        console.log('Loading existing draft:', draft);
        setGuideData({
          title: draft.title || itemName || '',
          content: draft.content || '',
          images: draft.images || [],
          newImages: [],
          hazards: draft.hazards || [],
          ppe: draft.ppe || [],
          quickFacts: draft.quickFacts || {
            powerLimits: '',
            serialNumber: '',
            calibrationDue: '',
            weight: '',
            dimensions: ''
          }
        });
        setIsDraft(true);
        setExistingGuideId(draft.id);
      } else {
        const publishedGuide = await getPublishedGuide(itemId);
        if (publishedGuide) {
          console.log('Loading published guide:', publishedGuide);
          setGuideData({
            title: publishedGuide.title || itemName || '',
            content: publishedGuide.content || '',
            images: publishedGuide.images || [],
            newImages: [],
            hazards: publishedGuide.hazards || [],
            ppe: publishedGuide.ppe || [],
            quickFacts: publishedGuide.quickFacts || {
              powerLimits: '',
              serialNumber: '',
              calibrationDue: '',
              weight: '',
              dimensions: ''
            }
          });
          setIsDraft(false);
          setExistingGuideId(publishedGuide.id);
        }
      }
    } catch (error) {
      console.error('Error loading draft:', error);
    }
  };

  const handleImageUpload = (event, isNew = true) => {
    const files = Array.from(event.target.files);
    const targetImages = isNew ? 'newImages' : 'images';
    const targetFiles = isNew ? newImageFiles : imageFiles;
    const setTargetFiles = isNew ? setNewImageFiles : setImageFiles;
    
    const newImages = files.map((file, index) => ({
      file,
      altText: '',
      caption: '',
      order: targetFiles.length + index
    }));
    
    setGuideData(prev => ({
      ...prev,
      [targetImages]: [...prev[targetImages], ...newImages]
    }));
    setTargetFiles([...targetFiles, ...files]);
  };

  const removeImage = (index, isNew = false) => {
    const targetImages = isNew ? 'newImages' : 'images';
    const targetFiles = isNew ? newImageFiles : imageFiles;
    const setTargetFiles = isNew ? setNewImageFiles : setImageFiles;
    
    setGuideData(prev => ({
      ...prev,
      [targetImages]: prev[targetImages].filter((_, i) => i !== index)
    }));
    setTargetFiles(targetFiles.filter((_, i) => i !== index));
  };

  const updateImageData = (index, field, value, isNew = false) => {
    const targetImages = isNew ? 'newImages' : 'images';
    setGuideData(prev => ({
      ...prev,
      [targetImages]: prev[targetImages].map((img, i) => 
        i === index ? { ...img, [field]: value } : img
      )
    }));
  };

  const insertImageIntoContent = (imageIndex, isNew = false) => {
    const targetImages = isNew ? 'newImages' : 'images';
    const image = guideData[targetImages][imageIndex];
    
    if (!image) return;
    
    // For existing images, use the actual URL
    // For new images, create a placeholder that will be replaced after upload
    let imageMarkdown;
    if (isNew) {
      // New images get placeholders
      imageMarkdown = `\n\n![${image.altText || 'Image'}](image_placeholder_${imageIndex}_new)`;
    } else {
      // Existing images use their actual URLs
      imageMarkdown = `\n\n![${image.altText || 'Image'}](${image.url})`;
    }
    
    if (image.caption) {
      imageMarkdown += `\n*${image.caption}*`;
    }
    
    if (contentRef.current) {
      const start = contentRef.current.selectionStart;
      const end = contentRef.current.selectionEnd;
      const text = guideData.content;
      const newText = text.substring(0, start) + imageMarkdown + text.substring(end);
      
      setGuideData(prev => ({ ...prev, content: newText }));
      
      // Set cursor position after the inserted image
      setTimeout(() => {
        const newPosition = start + imageMarkdown.length;
        contentRef.current.setSelectionRange(newPosition, newPosition);
        contentRef.current.focus();
      }, 0);
    }
  };

  const addNumberedStep = () => {
    const stepNumber = (guideData.content.match(/\d+\./g) || []).length + 1;
    const newStep = `\n${stepNumber}. `;
    
    if (contentRef.current) {
      const start = contentRef.current.selectionStart;
      const end = contentRef.current.selectionEnd;
      const text = guideData.content;
      const newText = text.substring(0, start) + newStep + text.substring(end);
      
      setGuideData(prev => ({ ...prev, content: newText }));
      
      // Set cursor position after the new step
      setTimeout(() => {
        contentRef.current.setSelectionRange(start + newStep.length, start + newStep.length);
        contentRef.current.focus();
      }, 0);
    }
  };

  const toggleHazard = (hazard) => {
    setGuideData(prev => ({
      ...prev,
      hazards: prev.hazards.includes(hazard)
        ? prev.hazards.filter(h => h !== hazard)
        : [...prev.hazards, hazard]
    }));
  };

  const togglePPE = (ppe) => {
    setGuideData(prev => ({
      ...prev,
      ppe: prev.ppe.includes(ppe)
        ? prev.ppe.filter(p => p !== ppe)
        : [...prev.ppe, ppe]
    }));
  };

  const updateQuickFact = (field, value) => {
    setGuideData(prev => ({
      ...prev,
      quickFacts: { ...prev.quickFacts, [field]: value }
    }));
  };

  const saveDraft = async () => {
    try {
      setLoading(true);
      setError('');
      
      const authorData = {
        authorId: 'admin', // This would come from auth context
        authorName: 'Administrator' // This would come from auth context
      };
      
      let updatedGuideData = { ...guideData };
      
      if (isDraft && existingGuideId) {
        // Update existing draft - only handle new images
        const result = await updateGuide(existingGuideId, guideData, newImageFiles);
        
        // Replace placeholders with actual URLs for new images only
        if (result.images && guideData.newImages.length > 0) {
          let updatedContent = guideData.content;
          guideData.newImages.forEach((image, index) => {
            const placeholder = `image_placeholder_${index}_new`;
            // Find the corresponding uploaded image by matching properties
            const actualImage = result.images.find(img => 
              img.altText === image.altText && 
              img.caption === image.caption &&
              img.order >= guideData.images.length // New images have higher order numbers
            );
            if (actualImage && actualImage.url) {
              updatedContent = updatedContent.replace(new RegExp(placeholder, 'g'), actualImage.url);
            }
          });
          updatedGuideData.content = updatedContent;
          
          // Move new images to existing images array and clear new images
          updatedGuideData.images = result.images;
          updatedGuideData.newImages = [];
          setNewImageFiles([]);
        }
      } else {
        // Create new guide
        const result = await createGuide(itemId, { ...guideData, ...authorData }, imageFiles);
        setExistingGuideId(result.id);
        setIsDraft(true);
        
        // Replace placeholders with actual URLs for uploaded images
        if (result.images) {
          let updatedContent = guideData.content;
          guideData.images.forEach((image, index) => {
            const placeholder = `image_placeholder_${index}_existing`;
            const actualImage = result.images[index];
            if (actualImage && actualImage.url) {
              updatedContent = updatedContent.replace(new RegExp(placeholder, 'g'), actualImage.url);
            }
          });
          updatedGuideData.content = updatedContent;
          updatedGuideData.images = result.images;
        }
      }
      
      // Update the guide data with the processed content
      setGuideData(updatedGuideData);
      
      setSuccess('Draft saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
      // Reload the most recent draft from Firestore
      await loadExistingDraft();
    } catch (error) {
      console.error('Save draft error:', error);
      setError('Failed to save draft: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const publishGuide = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Save draft first if needed
      if (!isDraft) {
        await saveDraft();
      }
      
      // Ensure we have a guide ID to publish
      if (!existingGuideId) {
        throw new Error('No guide to publish. Please save a draft first.');
      }
      
      console.log('Publishing guide with ID:', existingGuideId);
      
      // Publish the guide
      await publishGuideOperation(existingGuideId, 'admin', 'Administrator');
      
      // Delete ALL drafts for this item first
      await deleteAllDrafts(itemId);
      
      // Create a new draft copy for future editing
      const authorData = {
        authorId: 'admin',
        authorName: 'Administrator'
      };
      
      const newDraft = await createGuide(itemId, { ...guideData, ...authorData }, []);
      setExistingGuideId(newDraft.id);
      setIsDraft(true);
      
      setSuccess('Guide published successfully!');
      setTimeout(() => {
        setSuccess('');
        onSave && onSave();
      }, 2000);
      // Reload the most recent draft from Firestore
      await loadExistingDraft();
    } catch (error) {
      console.error('Publishing error:', error);
      setError('Failed to publish guide: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const renderPreview = () => {
    // Process content to replace image placeholders with actual URLs
    let processedContent = guideData.content;
    const newTempUrls = [];
    
    // Replace placeholders for new images with temporary URLs
    guideData.newImages.forEach((image, index) => {
      if (image.file) {
        const placeholder = `image_placeholder_${index}_new`;
        const tempUrl = URL.createObjectURL(image.file);
        newTempUrls.push(tempUrl);
        processedContent = processedContent.replace(new RegExp(placeholder, 'g'), tempUrl);
      }
    });

    // Update temp URLs state
    setTempUrls(prev => {
      // Revoke old URLs
      prev.forEach(url => URL.revokeObjectURL(url));
      return newTempUrls;
    });

    return (
      <div className="bg-white rounded-lg border p-6 h-full overflow-y-auto">
        <div className="prose prose-lg max-w-none">
          <ReactMarkdown
            components={{
              // Custom styling for images
              img: ({ src, alt, ...props }) => (
                <div className="my-4 text-center">
                  <img
                    src={src}
                    alt={alt || 'Equipment image'}
                    className="max-w-full h-auto rounded-lg shadow-md mx-auto"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      console.error('Failed to load image:', src);
                    }}
                    {...props}
                  />
                </div>
              ),
              // Custom styling for numbered lists (steps)
              ol: ({ children, ...props }) => (
                <ol className="space-y-4" {...props}>
                  {children}
                </ol>
              ),
              li: ({ children, ...props }) => (
                <li className="p-3 bg-blue-50 border-l-4 border-blue-500 rounded-r" {...props}>
                  <div className="font-medium text-blue-900">{children}</div>
                </li>
              ),
              // Custom styling for headings
              h1: ({ children, ...props }) => (
                <h1 className="text-2xl font-bold text-gray-900 mb-4" {...props}>
                  {children}
                </h1>
              ),
              h2: ({ children, ...props }) => (
                <h2 className="text-xl font-semibold text-gray-900 mb-3" {...props}>
                  {children}
                </h2>
              ),
              h3: ({ children, ...props }) => (
                <h3 className="text-lg font-medium text-gray-900 mb-2" {...props}>
                  {children}
                </h3>
              ),
              // Custom styling for paragraphs
              p: ({ children, ...props }) => (
                <p className="mb-3 text-gray-700 leading-relaxed" {...props}>
                  {children}
                </p>
              ),
              // Custom styling for strong text
              strong: ({ children, ...props }) => (
                <strong className="font-bold text-gray-900" {...props}>
                  {children}
                </strong>
              ),
              // Custom styling for emphasis
              em: ({ children, ...props }) => (
                <em className="italic text-gray-800" {...props}>
                  {children}
                </em>
              ),
              // Custom styling for code blocks
              code: ({ children, ...props }) => (
                <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono text-gray-800" {...props}>
                  {children}
                </code>
              ),
              // Custom styling for blockquotes
              blockquote: ({ children, ...props }) => (
                <blockquote className="border-l-4 border-yellow-500 pl-4 py-2 bg-yellow-50 italic text-gray-700" {...props}>
                  {children}
                </blockquote>
              ),
              // Custom styling for horizontal rules
              hr: ({ ...props }) => (
                <hr className="my-6 border-gray-300" {...props} />
              ),
              // Custom styling for links
              a: ({ children, href, ...props }) => (
                <a 
                  href={href} 
                  className="text-blue-600 hover:text-blue-800 underline" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  {...props}
                >
                  {children}
                </a>
              ),
              // Add missing components to prevent React errors
              ul: ({ children, ...props }) => (
                <ul className="list-disc list-inside space-y-2" {...props}>
                  {children}
                </ul>
              ),
              table: ({ children, ...props }) => (
                <table className="min-w-full border border-gray-300" {...props}>
                  {children}
                </table>
              ),
              th: ({ children, ...props }) => (
                <th className="border border-gray-300 px-4 py-2 bg-gray-100 font-medium" {...props}>
                  {children}
                </th>
              ),
              td: ({ children, ...props }) => (
                <td className="border border-gray-300 px-4 py-2" {...props}>
                  {children}
                </td>
              ),
            }}
          >
            {processedContent}
          </ReactMarkdown>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-black text-white px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-bold">Edit Guide: {itemName}</h2>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-300"
          >
            ✕
          </button>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div className="m-4 p-3 bg-green-100 border border-green-300 text-green-700 rounded-md">
            {success}
          </div>
        )}
        
        {error && (
          <div className="m-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-md">
            {error}
          </div>
        )}

        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column - Editor */}
            <div>
              {/* Title */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Guide Title
                </label>
                <input
                  type="text"
                  value={guideData.title}
                  onChange={(e) => setGuideData(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  placeholder="Enter guide title"
                />
              </div>

              {/* Content Editor */}
              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Guide Content (Markdown supported)
                  </label>
                  <button
                    onClick={addNumberedStep}
                    className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600"
                  >
                    Add Step
                  </button>
                </div>
                <textarea
                  ref={contentRef}
                  value={guideData.content}
                  onChange={(e) => setGuideData(prev => ({ ...prev, content: e.target.value }))}
                  className="w-full h-64 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 font-mono text-sm"
                  placeholder="Write your guide content here. Use numbered steps for better comprehension."
                />
              </div>

              {/* Image Upload */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload Images (Drag & Drop or Click)
                </label>
                <div
                  className="border-2 border-dashed border-gray-300 rounded-md p-4 text-center"
                  onDrop={(e) => {
                    e.preventDefault();
                    const files = Array.from(e.dataTransfer.files);
                    handleImageUpload({ target: { files } });
                  }}
                  onDragOver={(e) => e.preventDefault()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e)}
                    className="hidden"
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="px-4 py-2 bg-yellow-500 text-black rounded hover:bg-yellow-600"
                  >
                    Choose Images
                  </button>
                  <p className="text-sm text-gray-500 mt-2">
                    Drag and drop images here or click to browse
                  </p>
                </div>
              </div>

              {/* Image List */}
              {guideData.images.length > 0 && (
                <div className="mb-4">
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Uploaded Images</h3>
                  <div className="space-y-2">
                    {guideData.images.map((image, index) => (
                      <div key={index} className="flex items-center space-x-2 p-2 border rounded">
                        <img
                          src={image.url}
                          alt="Preview"
                          className="w-16 h-16 object-cover rounded"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            console.error('Failed to load image:', image.url);
                          }}
                        />
                        <div className="flex-1">
                          <input
                            type="text"
                            placeholder="Alt text"
                            value={image.altText || ''}
                            onChange={(e) => updateImageData(index, 'altText', e.target.value)}
                            className="w-full px-2 py-1 text-sm border rounded mb-1"
                          />
                          <input
                            type="text"
                            placeholder="Caption (optional)"
                            value={image.caption || ''}
                            onChange={(e) => updateImageData(index, 'caption', e.target.value)}
                            className="w-full px-2 py-1 text-sm border rounded"
                          />
                        </div>
                        <div className="flex flex-col space-y-1">
                          <button
                            onClick={() => insertImageIntoContent(index)}
                            className="px-2 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600"
                            title="Insert image into content"
                          >
                            Insert
                          </button>
                          <button
                            onClick={() => removeImage(index)}
                            className="text-red-500 hover:text-red-700 text-xs"
                            title="Remove image"
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* New Images List */}
              {guideData.newImages.length > 0 && (
                <div className="mb-4">
                  <h3 className="text-sm font-medium text-gray-700 mb-2">New Images</h3>
                  <div className="space-y-2">
                    {guideData.newImages.map((image, index) => (
                      <div key={index} className="flex items-center space-x-2 p-2 border rounded">
                        <img
                          src={image.file ? URL.createObjectURL(image.file) : image.url}
                          alt="Preview"
                          className="w-16 h-16 object-cover rounded"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            console.error('Failed to load new image:', image.file?.name);
                          }}
                        />
                        <div className="flex-1">
                          <input
                            type="text"
                            placeholder="Alt text"
                            value={image.altText || ''}
                            onChange={(e) => updateImageData(index, 'altText', e.target.value, true)}
                            className="w-full px-2 py-1 text-sm border rounded mb-1"
                          />
                          <input
                            type="text"
                            placeholder="Caption (optional)"
                            value={image.caption || ''}
                            onChange={(e) => updateImageData(index, 'caption', e.target.value, true)}
                            className="w-full px-2 py-1 text-sm border rounded"
                          />
                        </div>
                        <div className="flex flex-col space-y-1">
                          <button
                            onClick={() => insertImageIntoContent(index, true)}
                            className="px-2 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600"
                            title="Insert image into content"
                          >
                            Insert
                          </button>
                          <button
                            onClick={() => removeImage(index, true)}
                            className="text-red-500 hover:text-red-700 text-xs"
                            title="Remove image"
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Right Column - Safety & Quick Facts */}
            <div>
              {/* Quick Facts */}
              <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-3">Quick Facts</h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Power Limits</label>
                    <input
                      type="text"
                      value={guideData.quickFacts.powerLimits}
                      onChange={(e) => updateQuickFact('powerLimits', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      placeholder="e.g., 120V, 15A max"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Serial Number</label>
                    <input
                      type="text"
                      value={guideData.quickFacts.serialNumber}
                      onChange={(e) => updateQuickFact('serialNumber', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      placeholder="Equipment serial number"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Calibration Due</label>
                    <input
                      type="date"
                      value={guideData.quickFacts.calibrationDue}
                      onChange={(e) => updateQuickFact('calibrationDue', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Weight</label>
                    <input
                      type="text"
                      value={guideData.quickFacts.weight}
                      onChange={(e) => updateQuickFact('weight', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      placeholder="e.g., 25 lbs"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Dimensions</label>
                    <input
                      type="text"
                      value={guideData.quickFacts.dimensions}
                      onChange={(e) => updateQuickFact('dimensions', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      placeholder="e.g., 12in x 8in x 6in"
                    />
                  </div>
                </div>
              </div>

              {/* Hazards */}
              <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-3">Hazards</h3>
                <div className="grid grid-cols-2 gap-2">
                  {hazardOptions.map((hazard) => (
                    <label key={hazard} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={guideData.hazards.includes(hazard)}
                        onChange={() => toggleHazard(hazard)}
                        className="mr-2"
                      />
                      <span className="text-sm">{hazard}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* PPE */}
              <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-3">Required PPE</h3>
                <div className="grid grid-cols-2 gap-2">
                  {ppeOptions.map((ppe) => (
                    <label key={ppe} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={guideData.ppe.includes(ppe)}
                        onChange={() => togglePPE(ppe)}
                        className="mr-2"
                      />
                      <span className="text-sm">{ppe}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Preview Toggle */}
              <div className="mb-4">
                <button
                  onClick={() => setShowPreview(!showPreview)}
                  className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                >
                  {showPreview ? 'Hide Preview' : 'Show Preview'}
                </button>
              </div>

              {/* Preview */}
              {showPreview && (
                renderPreview()
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4 mt-6 pt-4 border-t">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={saveDraft}
              disabled={loading}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
            >
              {loading ? 'Saving...' : 'Save Draft'}
            </button>
            {isAdmin && (
              <button
                onClick={publishGuide}
                disabled={loading}
                className="px-4 py-2 bg-yellow-500 text-black rounded hover:bg-yellow-600 disabled:opacity-50"
              >
                {loading ? 'Publishing...' : 'Publish Guide'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuideEditor; 